import {
    DrawerFooter,
    DrawerHeader,
    Flex,
    HStack,
    ButtonGroup,
    DrawerBody,
    VStack,
    Button,
    InlineBanner,
    Text,
    Box,
    Input,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useManageUnitsDrawerData } from './use-manage-units-drawer-data';
import { ManageUnitsUnitGroupEnum, ManageUnitsUnitGroupEnumType } from '@mark43/cad-api';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { FilterPopover } from './common/filter-popover/filter-popover';
import { SortMenu } from './common/sort-menu';
import { UnitsSummary } from './common/units-summary';
import { EventSummary } from './components/event-summary';
import { convertManageUnitsUnitViewToPillUnit } from './convert-manage-units-unit-view-to-pill-unit';
import { GroupHeader } from './common/group-header';
import { BannersDisplay } from './common/banners-display';
import { globalAttributes } from '../common/global-attributes';
import { isNotNullOrUndefined } from '../queues/utils/is-not-null-or-undefined';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { UnitTile } from '../common/components/unit-tile';
import { useKeyboardNavigation } from '../common/hooks/use-tile-keyboard-navigation';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { v4 } from 'uuid';
import { createUnitSortComparator } from './utils';
import { useSubmitFormOnEnter } from '../common/hooks/use-submit-form-on-enter';
import { useApplicationType } from '../common/hooks/use-application-type';

export interface KeyboardNavState {
    index?: number;
    isOverPrimary: boolean;
}

export const ManageUnitsDrawer = observer(function ManageUnitsDrawer({
    eventId,
}: {
    eventId: number;
}): React.ReactElement {
    const drawerControls = useDrawerControls();

    const {
        store,
        event,
        debouncedSetQuery,
        sortingParam,
        setSortingParam,
        sortDirection,
        setSortDirection,
        selectedFilters,
        setSelectedFilters,
        hasSelectedFilters,
        toggleSelectUnit,
        initiallyDispatchedUnitIds,
        selectedUnitIds,
        releasedUnitIds,
        primaryUnitId,
        primaryDispositionInfo,
        setPrimaryUnitIdAndSelectIfNecessary,
        submit,
        banners,
        responsePlan,
        isEventQueryLoading,
        isUnitsQueryLoading,
        isUnitsQueryError,
        isMutationLoading,
        areUnitsFetchedAfterMount,
    } = useManageUnitsDrawerData(eventId);

    const openEventPanel = useOpenEventPanel();
    const { staffCountEnabled, unitLevelDispositionsEnabled } = useEnabledFeatures();

    const rows = store.data.list.slice();
    const groupCounts = store.data.groupCounts.slice();
    const groupHeaders = store.data.groupHeaders.slice();
    const [drawerContentElement, setDrawerContentElement] = useState<HTMLElement | null>(null);

    const alreadyDispatchedUnits = useMemo(
        () =>
            initiallyDispatchedUnitIds
                .map((id) => store.getItemById(id))
                .filter(isNotNullOrUndefined)
                .sort(createUnitSortComparator(primaryUnitId))
                .map(convertManageUnitsUnitViewToPillUnit),
        [initiallyDispatchedUnitIds, primaryUnitId, store]
    );

    const toDispatchUnits = useMemo(
        () =>
            selectedUnitIds
                .filter((id) => {
                    return !initiallyDispatchedUnitIds.includes(id);
                })
                .map((id) => store.getItemById(id))
                .filter(isNotNullOrUndefined)
                .sort(createUnitSortComparator(primaryUnitId))
                .map(convertManageUnitsUnitViewToPillUnit),
        [selectedUnitIds, initiallyDispatchedUnitIds, primaryUnitId, store]
    );

    const eventStaffCount = useMemo(() => {
        if (!staffCountEnabled) return undefined;

        return alreadyDispatchedUnits.reduce(
            (eventStaffCount, unit) => eventStaffCount + (unit.staffCount ?? 0),
            0
        );
    }, [alreadyDispatchedUnits, staffCountEnabled]);

    const canFocusPrimary = useCallback(() => {
        return true;
    }, []);

    const keyboardTileNavigation = useKeyboardNavigation({
        store,
        onSelectItem: toggleSelectUnit,
        onSetPrimaryItem: setPrimaryUnitIdAndSelectIfNecessary,
        element: drawerContentElement,
        canFocusPrimary,
    });

    const closeDrawer = useCallback(() => {
        drawerControls.close();
    }, [drawerControls]);

    const handleSubmit = useCallback(() => {
        submit({
            confirmationAcknowledged: false,
            assignAndHold: false,
        });
    }, [submit]);

    useSubmitFormOnEnter({
        element: drawerContentElement,
        onSubmit: handleSubmit,
    });

    const renderItemContent = (index: number): React.ReactElement => {
        const unit = store.data.list[index];

        if (!unit) {
            return <></>;
        }

        return (
            <Box paddingRight="16px">
                <UnitTile
                    unit={unit}
                    toggleSelect={toggleSelectUnit}
                    isSelected={selectedUnitIds.includes(unit.id)}
                    key={index}
                    isFocused={index === keyboardTileNavigation.currentIndex}
                    isFocusingLeft={keyboardTileNavigation.isFocusingLeft}
                    isFocusingRight={keyboardTileNavigation.isFocusingRight}
                    primaryUnitId={primaryUnitId}
                    setPrimaryUnitIdAndSelectIfNecessary={setPrimaryUnitIdAndSelectIfNecessary}
                    showStaffCount
                />
            </Box>
        );
    };

    function renderGroupContent(index: number): React.ReactElement {
        return (
            <GroupHeader
                displayName={
                    ManageUnitsUnitGroupEnum[groupHeaders[index] as ManageUnitsUnitGroupEnumType]
                        .displayName
                }
                count={groupCounts[index]}
            />
        );
    }

    const drawerTitle = (
        <Box mt="1" mb="3">
            <Text variant="headingMd">Manage Units</Text>
        </Box>
    );

    const [hasOpenedEventView, setHasOpenedEventView] = useState(false);
    const workspacePanelsStore = useWorkspaceStore();
    const { trigger } = useIntegratedActionBroadcaster();
    const { isElectron } = useApplicationType();

    useEffect(() => {
        const activeWorkspaceWindowType = workspacePanelsStore.activeWorkspaceWindowType;
        const baseShouldOpenEventPanelAndFocusUnitTab =
            !hasOpenedEventView &&
            // We want to wait for this request to finish before attempting to open
            // the unit tab because this request is the thing that actually
            // persists a unit recommendation (if there is one),
            // and we want to make sure that when we do open the unit tab,
            // the recommendation generated from this request is present
            areUnitsFetchedAfterMount &&
            (responsePlan ||
                event?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE);

        function openEventPanelAndFocusUnitTab() {
            openEventPanel({
                dontFocusWindow: true,
                eventId,
            });

            trigger([
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'SET_EVENT_PANEL_TAB',
                    payload: {
                        eventId,
                        tab: 'UNITS',
                    },
                },
            ]);

            setHasOpenedEventView(true);
        }

        if (baseShouldOpenEventPanelAndFocusUnitTab) {
            switch (activeWorkspaceWindowType) {
                // If we opened the MUD from a queue or from a map,
                // only open the events panel if we are in electron
                case 'QUEUE_V2':
                case 'MAP_V2': {
                    if (isElectron) {
                        openEventPanelAndFocusUnitTab();
                    }
                    break;
                }
                // If we opened the MUD from an event panel
                // then also open/focus the event panel and set the tab
                case 'EVENT_V2': {
                    openEventPanelAndFocusUnitTab();
                    break;
                }
            }
        }
    }, [
        hasOpenedEventView,
        areUnitsFetchedAfterMount,
        event,
        responsePlan,
        eventId,
        openEventPanel,
        trigger,
        workspacePanelsStore,
        isElectron,
    ]);

    return (
        <Drawer
            isOpen
            size="lg"
            variant="overlay"
            onClose={closeDrawer}
            closeOnOverlayClick={false}
        >
            <Flex height="100%" direction="column" ref={setDrawerContentElement}>
                {isEventQueryLoading ? (
                    <>
                        <DrawerHeader>{drawerTitle}</DrawerHeader>
                        <DrawerBody>
                            <LoadingIndicator />
                        </DrawerBody>
                    </>
                ) : (
                    <>
                        <DrawerHeader>
                            {drawerTitle}
                            {event && (
                                <>
                                    <VStack align="left">
                                        <EventSummary
                                            {...(staffCountEnabled ? { eventStaffCount } : {})}
                                            event={event}
                                            resourcePatternString={
                                                responsePlan?.resourcePatternString
                                            }
                                        />
                                        <Box marginTop="0">
                                            {!!initiallyDispatchedUnitIds.length && (
                                                <UnitsSummary
                                                    label="Already Dispatched"
                                                    units={alreadyDispatchedUnits}
                                                    ghostUnitIds={releasedUnitIds}
                                                />
                                            )}
                                            {!isUnitsQueryLoading && (
                                                <UnitsSummary
                                                    label="To Dispatch"
                                                    units={toDispatchUnits}
                                                />
                                            )}
                                        </Box>
                                    </VStack>
                                    <Flex my="3" flexDirection="column" gap="half">
                                        <Input
                                            isClearable
                                            autoFocus
                                            data-testid="manage-units-text-filter-input"
                                            leftIcon="Search"
                                            size="sm"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                debouncedSetQuery(e.currentTarget.value);
                                            }}
                                        />
                                        <Text color="tertiary" variant="bodyMd">
                                            Search by Unit Call Sign, Location, Status, Type, or
                                            Skill
                                        </Text>
                                    </Flex>
                                    <HStack gap="1">
                                        <FilterPopover
                                            selectedFilters={selectedFilters}
                                            setSelectedFilters={setSelectedFilters}
                                            hasSelectedFilters={hasSelectedFilters}
                                        />
                                        <SortMenu
                                            sortDirection={sortDirection}
                                            sortingParam={sortingParam}
                                            onChangeSortDirection={setSortDirection}
                                            onChangeSortingParam={setSortingParam}
                                        />
                                    </HStack>
                                </>
                            )}
                            {unitLevelDispositionsEnabled &&
                                primaryDispositionInfo &&
                                primaryDispositionInfo.isPrimary &&
                                primaryDispositionInfo.unitCallSign &&
                                !primaryUnitId && (
                                    <BannersDisplay
                                        banners={[
                                            {
                                                status: 'info',
                                                title: 'Primary unit dispositioned',
                                                description: (
                                                    <Flex>
                                                        <Text variant="headingSm">
                                                            {`${primaryDispositionInfo.unitCallSign}`}
                                                        </Text>
                                                        &nbsp;
                                                        <Text>{` - ${
                                                            primaryDispositionInfo
                                                                .dispositionAttributeOption
                                                                ?.label ?? ''
                                                        }`}</Text>
                                                    </Flex>
                                                ),
                                            },
                                        ]}
                                    />
                                )}
                            <BannersDisplay banners={banners} />
                        </DrawerHeader>
                        <DrawerBody
                            style={{ paddingTop: '8px', paddingRight: 0, paddingBottom: 0 }}
                        >
                            {isUnitsQueryError && (
                                <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
                                    <InlineBanner
                                        status="error"
                                        description="An error occurred while loading data"
                                    />
                                </Flex>
                            )}
                            {isUnitsQueryLoading ? (
                                <LoadingIndicator />
                            ) : (
                                <GroupedVirtuoso
                                    ref={keyboardTileNavigation.virtuosoRef}
                                    tabIndex={-1}
                                    groupCounts={groupCounts}
                                    groupContent={renderGroupContent}
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                    }}
                                    rows={rows.length}
                                    itemContent={renderItemContent}
                                />
                            )}
                        </DrawerBody>
                    </>
                )}
                <DrawerFooter>
                    <ButtonGroup>
                        <Button
                            onFocus={keyboardTileNavigation.clearKeyboardNavState}
                            variant="ghost"
                            onClick={closeDrawer}
                            isDisabled={isMutationLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            isDisabled={isEventQueryLoading || isUnitsQueryLoading}
                            isLoading={isMutationLoading}
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </ButtonGroup>
                </DrawerFooter>
            </Flex>
        </Drawer>
    );
});

export type ManageUnitsDrawerType = typeof ManageUnitsDrawer;
